import ProductInterface from '@/interfaces/ProductInterface';
import ProductPriceInterface from '@/interfaces/ProductPriceInterface';
import ProductPriceMappedDataInterface from '@/interfaces/ProductPriceMappedDataInterface';
import BonusInterface from '@/interfaces/BonusInterface';
import { formatNumber } from '@/helpers';
import ProductCategoryInterface from '@/interfaces/ProductCategoryInterface';
import ProductImage from '@/interfaces/ProductImageInterface';
import ProductDocumentInterface from '@/interfaces/ProductDocumentInterface';

export default class Product {
  attributes: ProductInterface;

  priceKeys: string[];

  priceFormats: Record<'multiply' | 'ceil', Record<string, number>>;

  translations: Record<'branch', Record<string, string>>;

  useEco: boolean;

  constructor(attributes: ProductInterface) {
    this.attributes = attributes;

    if (this.attributes.price) {
      this.useEco = `${this.attributes.price.params.bio}` === '1';
    } else if (this.attributes.priceEco) {
      this.useEco = `${this.attributes.priceEco.params.bio}` === '1';
    } else {
      this.useEco = false;
    }

    this.priceKeys = [
      'workingPrice',
      'workingPriceHt',
      'workingPriceNt',
      'basePrice',
      'yearly',
      'monhtly',
      'savingsYearly',
      'savingsMonthly',
    ];

    this.priceFormats = {
      multiply: {
        workingPrice: 100,
        workingPriceHt: 100,
        workingPriceNt: 100,
      },
      ceil: {
        monthly: 1,
      },
    };

    this.translations = {
      branch: {
        electricity: 'Strom',
        power: 'Strom',
        gas: 'Gas',
        heat: 'Heizstrom',
      },
    };
  }

  get productType(): string {
    return this.attributes.category.isCommodity ? 'commodity' : 'catalog';
  }

  get price(): ProductPriceInterface | undefined {
    if (this.useEco) {
      return this.attributes.priceEco;
    }

    return this.attributes.price;
  }

  set price(price: ProductPriceInterface | undefined) {
    this.attributes.price = price;
    if (price) {
      this.useEco = `${price.params.bio}` === '1';
    }
  }

  set priceEco(priceEco: ProductPriceInterface | undefined) {
    this.attributes.priceEco = priceEco;
    if (priceEco) {
      this.useEco = `${priceEco.params.bio}` === '1';
    }
  }

  get hasEco(): boolean {
    return this.attributes.price !== undefined
      && this.attributes.priceEco !== undefined;
  }

  get ecoDifference(): number {
    if (this.hasEco) {
      return (this.attributes.priceEco?.mapped.active.monthlyWithBonus || 2) - (this.attributes.price?.mapped.active.monthlyWithBonus || 1);
    }

    return 0;
  }

  get ecoDifferenceGross(): number {
    if (this.hasEco) {
      return (this.attributes.priceEco?.mapped.active.monthlyWithBonusGross || 2) - (this.attributes.price?.mapped.active.monthlyWithBonusGross || 1);
    }

    return 0;
  }

  get priceCustomerType(): string {
    return this.price?.params?.customerType || 'private';
  }

  get priceIsDouble(): boolean {
    return !!this.attributes?.price?.params?.double;
  }

  get appliedBonuses(): BonusInterface[] {
    return this.attributes?.price?.mapped?.active?.appliedBonus || [];
  }

  get name(): string {
    let formattedProductName = this.attributes.name;

    formattedProductName = formattedProductName.replace('StadtwerkeBochum ', 'Stadtwerke<b>Bochum</b>&nbsp;');

    return formattedProductName;
  }

  get id(): number {
    return this.attributes.id;
  }

  get benefits(): string[] {
    return this.attributes.benefits || [];
  }

  get branchTranslated(): string {
    return this.translations.branch[this.branch];
  }

  get isRecommended(): boolean {
    return this.attributes.recommended || false;
  }

  get category(): ProductCategoryInterface {
    return this.attributes.category;
  }

  get branch(): string {
    return this.category.key;
  }

  public getRecommendationText(isSingle = false): string | null {
    if (this.isRecommended) {
      return isSingle ? (
        this.attributes.recommendationTextSingle
        || this.attributes.recommendationText
        || null
      ) : (
        this.attributes.recommendationText
        || null
      );
    }

    return null;
  }

  get slogan(): string | null {
    return this.attributes.slogan || null;
  }

  get priceFileUrl(): string | null {
    return this.price?.pricefile || null;
  }

  get logoUrl(): string | null {
    let sourceUrl = this.attributes.thumbnail;
    const images = Object.values(this.attributes.details?.media?.images || {});

    if (sourceUrl === null && images.length) {
      let logoSource = images.find((m: ProductImage) => m.key === 'logo');

      if (logoSource) {
        sourceUrl = `${logoSource.url || ''}`.trim();
      }

      if (sourceUrl === null || sourceUrl.length === 0) {
        logoSource = images.find((m: ProductImage) => m.isPreview);

        if (logoSource) {
          sourceUrl = `${logoSource.url || ''}`.trim();
        }
      }
    }

    return sourceUrl || null;
  }

  public getDocumentByKey(key: string): ProductDocumentInterface | null {
    return Object.values(this.attributes.details.media?.documents ?? {}).find((m: ProductDocumentInterface) => m.key === key) || null;
  }

  public getDocumentUrlByKey(key: string): string {
    return this.getDocumentByKey(key)?.url || '#';
  }

  get priceUsageFormatted(): string {
    return formatNumber(this.price?.params.usage || 0, 0);
  }

  get hasPriceGuarantee(): boolean {
    return this.attributes.details.contractDetails.hasPriceGuarantee || false;
  }

  get contractTermIsFixed(): boolean {
    return this.attributes.details.contractDetails.contractTermMonths === null && !!this.attributes.details.contractDetails.contractTermFixed;
  }

  get contractTermLabel(): string {
    if (
      this.contractTermIsFixed
      && this.attributes.details.contractDetails.priceGuaranteeTermFixed
    ) {
      const contractTermDateString = this.attributes.details.contractDetails.contractTermFixed || '';
      const contractTermDate = new Date(contractTermDateString);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return `${`${contractTermDate}` !== 'Invalid Date' ? contractTermDate.format('d.m.Y') : contractTermDateString}`;
    }

    return `${this.attributes.details.contractDetails.contractTermMonths} Monat${(this.attributes.details.contractDetails.contractTermMonths || 0) > 1 ? 'e' : ''}`;
  }

  public setEcoParam(eco: boolean): void {
    this.useEco = eco;
    if (this.attributes.price) {
      this.attributes.price.params.bio = eco ? 1 : 0;
    }
    if (this.attributes.priceEco) {
      this.attributes.priceEco.params.bio = eco ? 1 : 0;
    }
  }

  get priceGuaranteeIsFixed(): boolean {
    return this.attributes.details.contractDetails.priceGuaranteeTermMonths === null && !!this.attributes.details.contractDetails.priceGuaranteeTermFixed;
  }

  get priceGuaranteeLabel(): string {
    if (
      this.priceGuaranteeIsFixed
      && this.attributes.details.contractDetails.priceGuaranteeTermFixed
    ) {
      const priceGuaranteeDateString = this.attributes.details.contractDetails.priceGuaranteeTermFixed;
      const priceGuaranteeDate = new Date(priceGuaranteeDateString);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return `${`${priceGuaranteeDate}` !== 'Invalid Date' ? priceGuaranteeDate.format('d.m.Y') : priceGuaranteeDateString}`;
    }

    return this.attributes.details.contractDetails.priceGuaranteeTermMonths ? `${this.attributes.details.contractDetails.priceGuaranteeTermMonths} Monate` : 'Keine';
  }

  private applyPriceFormats(key: string, value: number, withBonus = false) {
    let formatKey = key;

    if (withBonus) {
      formatKey = `${formatKey}WithBonus`;
    }

    const valueFormatted = value * (this.priceFormats.multiply[formatKey] || this.priceFormats.multiply[key] || 1);
    const ceilingValue = this.priceFormats.ceil[formatKey] || valueFormatted;

    return Math.ceil(valueFormatted / ceilingValue) * ceilingValue;
  }

  public getPriceFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, this.getPriceValue(key, priceType), true), digits);
  }

  public getAbsPriceFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, Math.abs(this.getPriceValue(key, priceType)), true), digits);
  }

  public getPriceWithoutBonusFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, this.getPriceValueWithoutBonus(key, priceType), false), digits);
  }

  public getAbsPriceWithoutBonusFormatted(key: string, digits = 2, priceType: 'gross' | 'net' | null = null): string {
    return formatNumber(this.applyPriceFormats(key, Math.abs(this.getPriceValueWithoutBonus(key, priceType)), false), digits);
  }

  public getPriceValue(key: string, priceType: 'gross' | 'net' | null = null): number {
    let value = 0;

    if (key === 'eco') {
      return this.priceCustomerType === 'private' || priceType === 'gross' ? this.ecoDifferenceGross : this.ecoDifference;
    }

    if (this.priceCustomerType === 'private' || priceType === 'gross') {
      const valGross = this.price?.mapped.active[`${key}WithBonusGross` as keyof ProductPriceMappedDataInterface]
        || this.getPriceValueWithoutBonus(key);
      if (typeof valGross === 'number') {
        value = valGross;
      }
    } else if (this.priceCustomerType === 'business' || priceType === 'net') {
      const valNet = this.price?.mapped.active[`${key}WithBonus` as keyof ProductPriceMappedDataInterface]
        || this.getPriceValueWithoutBonus(key);

      if (typeof valNet === 'number') {
        value = valNet;
      }
    }

    return value;
  }

  public getPriceValueWithoutBonus(key: string, priceType: 'gross' | 'net' | null = null): number {
    let value = 0;

    if (this.priceCustomerType === 'private' || priceType === 'gross') {
      const valGross = this.price?.mapped.active[`${key}Gross` as keyof ProductPriceMappedDataInterface] || 0;
      if (typeof valGross === 'number') {
        value = valGross;
      }
    } else if (this.priceCustomerType === 'business' || priceType === 'net') {
      const valNet = this.price?.mapped.active[`${key}` as keyof ProductPriceMappedDataInterface] || 0;

      if (typeof valNet === 'number') {
        value = valNet;
      }
    }

    return value;
  }
}
